import React, { useState } from 'react';
import { Box, Button, TextField, Stack, IconButton, Tooltip, Typography } from '@mui/material';
import { SearchOutlined, ClearOutlined, PrecisionManufacturingOutlined } from '@mui/icons-material';
import { useFacilityMaintenanceEditor } from '../../../../hooks/useFacilityMaintenanceEditor';
import { AssetPickerDialog } from '../../../shared/AssetPickerDialog';
import { AssetListView } from '@trackly/shared';
import { EditorMode } from '../../../../types/editorTypes';

interface Props {
  mode: EditorMode;
}

interface AssetPickerButtonProps {
  centerId: string;
  hasAsset: boolean;
  onClick: () => void;
}

// Mirrors pmDetails/AssetLookupEditor.tsx's own extraction of the same shape (Code Health).
const AssetPickerButton: React.FC<AssetPickerButtonProps> = ({ centerId, hasAsset, onClick }) => (
  <Box>
    <Tooltip title={!centerId ? 'Select a center first' : ''}>
      <span>
        <Button
          variant="outlined"
          size="small"
          startIcon={hasAsset ? <PrecisionManufacturingOutlined /> : <SearchOutlined />}
          onClick={onClick}
          disabled={!centerId}
          sx={{ height: 40, whiteSpace: 'nowrap', fontWeight: 600, borderColor: '#e2e8f0', color: '#1e293b', '&:hover': { borderColor: '#3b82f6', backgroundColor: '#f0f7ff' } }}
        >
          {hasAsset ? 'Change Asset' : 'Select Asset'}
        </Button>
      </span>
    </Tooltip>
    {!centerId && (
      <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#ef4444' }}>
        Select a center first
      </Typography>
    )}
  </Box>
);

interface AssetInfo {
  assetId?: string;
  assetTag?: string;
  assetName?: string;
}

interface AssetDetailFieldsProps {
  assetInfo: AssetInfo;
  onClear: () => void;
}

// The `||` defaults live here, not in the parent, so the parent's own cyclomatic complexity stays
// low (mirrors pmDetails/AssetLookupEditor.tsx's own split, which this Code Health finding is
// otherwise identical to).
const AssetDetailFields: React.FC<AssetDetailFieldsProps> = ({ assetInfo, onClear }) => (
  <>
    <Box sx={{ flex: 1 }}>
      <TextField size="small" fullWidth label="Asset ID" value={assetInfo.assetId || ''} disabled slotProps={{ input: { readOnly: true } }} />
    </Box>
    <Box sx={{ flex: 1 }}>
      <TextField size="small" fullWidth label="Asset Tag" value={assetInfo.assetTag || ''} disabled slotProps={{ input: { readOnly: true } }} />
    </Box>
    <Box sx={{ flex: 1 }}>
      <TextField size="small" fullWidth label="Asset Name" value={assetInfo.assetName || ''} disabled slotProps={{ input: { readOnly: true } }} />
    </Box>
    <Tooltip title="Clear asset selection">
      <IconButton onClick={onClear} size="small" sx={{ mt: 0.5 }}>
        <ClearOutlined sx={{ fontSize: 18, color: '#64748b' }} />
      </IconButton>
    </Tooltip>
  </>
);

/**
 * Asset picker for the Facility Maintenance ("work order") ticket-form section (T077, FR-016) -
 * mirrors pmDetails/AssetLookupEditor.tsx, binding to facilityMaintenanceRequest.assetId/
 * assetTag/assetName (T075) instead of pmDetails.assetId, so a work order created from the
 * web is also visible in that asset's scan-triggered history on mobile (research.md item 9).
 * Deliberately does not also render AssetDocumentsPanel - that stays a Preventive-Maintenance-
 * only surface (FR-018's scope note), not part of this requirement.
 */
export const FacilityMaintenanceAssetLookupEditor: React.FC<Props> = ({ mode }) => {
  const { request, updateRequest } = useFacilityMaintenanceEditor(mode);
  const [dialogOpen, setDialogOpen] = useState(false);

  const centerId = request?.center || '';
  const assetInfo: AssetInfo = { assetId: request?.assetId, assetTag: request?.assetTag, assetName: request?.assetName };
  const hasAsset = !!assetInfo.assetId;

  const handleSelect = (asset: AssetListView) => {
    updateRequest({ assetId: asset.id, assetTag: asset.assetTag, assetName: asset.name });
  };

  const handleClear = () => {
    updateRequest({ assetId: '', assetTag: '', assetName: '' });
  };

  return (
    <Box>
      <Stack direction="row" spacing={2} alignItems="flex-start">
        <AssetPickerButton centerId={centerId} hasAsset={hasAsset} onClick={() => setDialogOpen(true)} />
        {hasAsset && <AssetDetailFields assetInfo={assetInfo} onClear={handleClear} />}
      </Stack>

      <AssetPickerDialog open={dialogOpen} onClose={() => setDialogOpen(false)} onSelect={handleSelect} centerId={centerId} />
    </Box>
  );
};

export default FacilityMaintenanceAssetLookupEditor;